import React, { useState, useMemo, useCallback } from 'react';
import { Box, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
//
import FilterPanel from './FilterPanel';
import Title from 'components/common/Title';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { TOP_UP_REQUESTS_COLUMN_DEFS } from 'utils/tables/columns/topUpRequests';
import { showErrorNotification } from 'utils/common';
import { useHasPermissions } from 'hooks/useHasPermissions';
import { PERMISSIONS } from '../../../constants';
import {
  useCancelPublisherTransactionMutation,
  useCreatePublisherTransactionMutation,
  useDownloadTransactionsMutation,
  useGetUGWTransactionsQuery,
} from '../../../store/api/transaction.api';
import { useBlobDownload } from 'hooks/utils/useBlobDownload';
import { DEFAULT_DATE_FORMAT } from '../../../constants/reports';
import { useDefaultReportRange } from 'hooks/utils/useDefaultReportRange';
import DateFnsUtils from '@date-io/date-fns';
import { useSnackbar } from 'notistack';

const StyledTableContainer = styled(Box)(() => ({
  width: '100%',
  ['& tbody .MuiTableCell-body']: {
    whiteSpace: 'nowrap',
  },
}));
const dateFns = new DateFnsUtils();

const TransactionHistoryTable = (props) => {
  const canEditTransactions = useHasPermissions(
    PERMISSIONS.TRANSACTIONS_MANAGEMENT,
  );
  const { startDate, endDate } = useDefaultReportRange();
  const { enqueueSnackbar } = useSnackbar();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [sorting, setSorting] = useState([{ id: 'createdAt', desc: true }]);

  const [filters, setFilters] = useState({ startDate, endDate });
  const [showCreatePaymentDialog, setShowCreatePaymentDialog] = useState(false);
  const [showCancelTopUpDialog, setShowCancelTopUpDialog] = useState(false);

  const baseQueryParams = useMemo(
    () => ({
      ...filters,
      startDate: dateFns.format(filters.startDate, DEFAULT_DATE_FORMAT),
      endDate: dateFns.format(filters.endDate, DEFAULT_DATE_FORMAT),
    }),
    [filters],
  );
  const downloadReportParams = useMemo(
    () => ({
      ...baseQueryParams,
      sorting,
    }),
    [baseQueryParams, sorting],
  );
  const fileName = useMemo(
    () =>
      `ubidex-top-up-requests-${Date.now()}-${baseQueryParams.startDate}-${baseQueryParams.endDate}.csv`,
    [baseQueryParams.startDate, baseQueryParams.endDate],
  );

  const showReportParams = useMemo(
    () => ({
      ...downloadReportParams,
      page: pagination.pageIndex,
      limit: pagination.pageSize,
    }),
    [downloadReportParams, pagination.pageIndex, pagination.pageSize],
  );

  const {
    data: { transactions = [], count = 0, totals = null } = {},
    isFetching,
  } = useGetUGWTransactionsQuery(showReportParams);
  const [downloadTransactions, { isLoading: isDownloadingReport }] =
    useDownloadTransactionsMutation();
  const [createPublisherTransaction] = useCreatePublisherTransactionMutation();
  const [cancelPublisherTransaction] = useCancelPublisherTransactionMutation();
  const downloadBlob = useBlobDownload();

  const isRequestPending = isFetching || isDownloadingReport;

  const extractTransactionNumbers = () =>
    table
      .getSelectedRowModel()
      .rows.map((row) => row.original.transactionNumber);

  const applyFilters = useCallback(
    (filters) => setFilters({ ...filters, ts: Date.now() }),
    [setFilters],
  );

  const onToggleOpenCreatePaymentDialog = () =>
    setShowCreatePaymentDialog(!showCreatePaymentDialog);
  const onToggleOpenCancelTopUpDialog = () =>
    setShowCancelTopUpDialog(!showCancelTopUpDialog);

  const handleTransactionAction = async (actionFn, onCloseDialog) => {
    onCloseDialog();
    const selectedTransactionNumbers = extractTransactionNumbers();
    try {
      await actionFn(selectedTransactionNumbers);
      table.resetRowSelection();
      enqueueSnackbar('Success', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.data?.message || err.message || 'Action failed', {
        variant: 'error',
      });
    }
  };

  const handleConfirm = () =>
    handleTransactionAction(
      (payload) => createPublisherTransaction(payload).unwrap(),
      () => setShowCreatePaymentDialog(false),
    );

  const handleCancelTopUp = () =>
    handleTransactionAction(
      (numbers) => cancelPublisherTransaction(numbers).unwrap(),
      () => setShowCancelTopUpDialog(false),
    );

  const handleDownloadTopUpReport = useCallback(async () => {
    try {
      const data = await downloadTransactions(downloadReportParams).unwrap();
      downloadBlob(data, fileName);
    } catch (err) {
      let errorMessage =
        'Unexpected error occurred while downloading the report. Try again later.';

      if (err.response) {
        const blob = err.response.data;
        if (blob instanceof Blob) {
          const errorText = await blob.text();

          const parsedError = JSON.parse(errorText);

          errorMessage = parsedError.error;
        }
      }

      showErrorNotification(errorMessage);
    }
  }, [downloadTransactions, downloadReportParams, downloadBlob, fileName]);

  const columns = useMemo(() => TOP_UP_REQUESTS_COLUMN_DEFS, []);

  const table = useMaterialReactTable({
    columns,
    data: transactions,
    rowCount: count,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableFilters: false,
    columnFilterDisplayMode: 'popover',
    positionToolbarAlertBanner: 'bottom',
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    meta: {
      totals,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [100, 500, 1000],
    },
    state: {
      isLoading: isRequestPending,
      pagination,
      sorting,
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const hasSelection = table.getSelectedRowModel().rows.length > 0;

      return (
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            padding: '8px',
            flexWrap: 'wrap',
          }}
        >
          {hasSelection && canEditTransactions && (
            <>
              <Button
                onClick={onToggleOpenCreatePaymentDialog}
                startIcon={<PaymentIcon />}
              >
                Create Payment
              </Button>

              <Button
                onClick={onToggleOpenCancelTopUpDialog}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            </>
          )}
          <Button
            disabled={table.getPrePaginationRowModel().rows.length === 0}
            onClick={handleDownloadTopUpReport}
            startIcon={<FileDownloadIcon />}
          >
            Export data
          </Button>
        </Box>
      );
    },
  });

  return (
    <Paper>
      <div style={{ width: '100%', marginTop: '15px', marginBottom: '15px' }}>
        <Box sx={{ pt: 2, px: 2 }}>
          <Title>Top Up Requests History</Title>
        </Box>
        <Box>
          <FilterPanel
            clientOptionsData={props.clientOptionsData}
            publisherOptionsData={props.publisherOptionsData}
            endpointOptionsData={props.endpointOptionsData}
            applyFilters={applyFilters}
            initialValues={filters}
          />
        </Box>
        <Box sx={{ pt: 2, px: 2, paddingBottom: '15px' }}>
          <ConfirmationDialog
            title='Create Payment'
            contentText='By clicking "Approve", you confirm that you have received money from the Client and initiate the payment process to the Publisher. Are you sure?'
            isOpen={showCreatePaymentDialog}
            handleConfirm={handleConfirm}
            handleClose={onToggleOpenCreatePaymentDialog}
          />

          <ConfirmationDialog
            title='Cancel Payment'
            contentText='By clicking "Approve", you confirm that you cancel top up request. Are you sure?'
            isOpen={showCancelTopUpDialog}
            handleConfirm={handleCancelTopUp}
            handleClose={onToggleOpenCancelTopUpDialog}
          />

          <StyledTableContainer>
            <MaterialReactTable table={table} />
          </StyledTableContainer>
        </Box>
      </div>
    </Paper>
  );
};

export default TransactionHistoryTable;
